import { MonthDate } from "./date";

export interface TimeSeries {
    date: string; // Format: "2025-05"
    score: number;
}

export class ChartData {
    month: string;
    value: number;
    fullDate: string;

    /**
     * Creates a new ChartData object.
     * @param {string} month - The month of the chart data point (e.g. "Jan", "Jun", etc.)
     * @param {number} value - The value of the chart data point
     * @param {string} fullDate - The full date of the chart data point in the format "YYYY-MM"
    */
    constructor(month: string, value: number, fullDate: string) {
        this.month = month;
        this.value = value;
        this.fullDate = fullDate; // Keep original date format for hover
    }

    static fromTimeSeries(ts: TimeSeries): ChartData {
        const month = MonthDate.parseYearMonth(ts.date)?.formatMonth() ?? ts.date;
        return new ChartData(month, ts.score, ts.date);
    }
}

/**
 * Computes the chart data and the current score from the given time series data.
 * @param {TimeSeries[]} data - the time series data
 * @returns {{ chartData: ChartData[]; currentScore: number | null }}
 * @description This function takes in time series data and returns the chart data points and the current score.
 * The current score is either the score of the closest month to today or the score of the last month in the data.
 */
export function computeChartState(
    data: TimeSeries[],
): { chartData: ChartData[]; currentScore: number | null } {
    const chartData = data.map((item) => ChartData.fromTimeSeries(item));

    const monthDates = chartData
        .map((item) => MonthDate.parseYearMonth(item.fullDate))
        .filter((d): d is MonthDate => d !== null);

    const closestMonth = MonthDate.getClosestToToday(monthDates);

    const closestScore =
        closestMonth !== null
            ? chartData.find((it) => it.fullDate === closestMonth.toString())
                ?.value ?? null
            : null;

    const fallbackScore =
        chartData[chartData.length - 1]?.value ?? null;

    const currentScore = closestScore ?? fallbackScore;

    return { chartData, currentScore };
}